import { Code, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import type { UseRowSelectionReturn } from '../../../hooks/useRowSelection';
import { mutate } from '../../../util/api/mutate';
import { usePercentagePriceChangeForm } from '../hooks/usePercentagePriceChangeForm';
import type {
	BranchSkuidPairDto,
	PercentagePriceChangeDto,
} from '../types/ProductPriceDto';
import { PercentagePriceChangeForm } from './PercentagePriceChangeForm';

interface PercentagePriceChangeModalProps {
	close: () => void;
	isOpened: boolean;
	onSuccess: () => void;
	rowSelection: UseRowSelectionReturn<BranchSkuidPairDto>;
	urlParams: string;
}

export function PercentagePriceChangeModal({
	isOpened,
	close,
	onSuccess,
	rowSelection,
	urlParams
}: PercentagePriceChangeModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = usePercentagePriceChangeForm();

	const percentageChangeMutation = mutate<PercentagePriceChangeDto, number>(
		`${organisation}/product-price/edit/bulk/percentage?${urlParams}`,
		{
			...inputs,
			keys: rowSelection.selectedKeys,
			allSelected: rowSelection.allSelectedMode,
		},
	);

	const resetAndClose = () => {
		form.reset();

		close();
		closeSuccessModal();
	};

	const complete = () => {
		onSuccess();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={
					<>
						<Title label={'Percentage Price Change'} />
						<Code>
							{`${rowSelection.selectedCount} item${rowSelection.selectedCount === 1 ? '' : 's'} selected.`}
						</Code>
					</>
				}
				withCloseButton={true}
				closeButtonProps={{
					style: { alignSelf: 'flex-start' },
				}}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={percentageChangeMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Prices Updated!'}
					body={`${rowSelection.selectedCount} item${rowSelection.selectedCount === 1 ? ' has' : 's have'} been updated. Click Ok to continue...`}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await percentageChangeMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<PercentagePriceChangeForm form={form} />
					<FormButtons
						close={resetAndClose}
						confirmModalProps={{
							title: 'Confirm percentage price change',
							body: (
								<>
									Apply percentage change to {rowSelection.selectedCount} item
									{rowSelection.selectedCount === 1 ? '' : 's'}? Click OK to
									confirm...
								</>
							),
						}}
					/>
				</form>
			</Modal>
		</>
	);
}
